import React, {useEffect, useState, useLayoutEffect} from 'react';
import {Col, Container, Row} from 'reactstrap';
import classNames from 'classnames';
import queryString from 'query-string';
import ABTest, {DebugMenu} from 'react-controlled-ab';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';

import {usePhaseState, usePhaseDispatch} from 'funnel-schedule/phase-context';
import {useUserState} from 'contexts/user';
import useBalanceText from 'utils/useBalanceText';
import track from 'utils/track';

import PageMeta from 'components/PageMeta';
import {SectionTriangle} from 'components/Elements/Section';
import HeaderVideo from 'components/Elements/HeaderVideo';
import CountdownTimer from 'components/Elements/CountdownTimer';
import CheckoutButton from 'components/CheckoutButton';
import {SalesDisclaimer} from 'components/SalesCta';
import SalesCopy from 'components/SalesCopy';
import ModalExitSales from 'components/Modals/ModalExitSales';
import ShareButtons from 'components/Social/ShareButtons';
import Footer from 'components/Elements/Footer';

export default function SalesIndex({
  noExitIntent = false,
  noFixedVideo = true,
  noShareButtons = false,
  ctaOverride = null,
  videoUrlOverride = '',
  ctaNoTimer = false,
  timerOverride = null,
  variant = null,
  pageTitle = 'Immerse Yourself In The World of Mushrooms With The Fantastic Fungi Impact Kit!',
  pageDescription = 'Fungi are solving some of the world’s toughest problems, including climate change, pollution, mental health, and more. Get the Fantastic Fungi Impact Kit to discover this underground healing movement!',
  shareUrl = 'https://fungi.foodrevolution.org/impact/',
  ogImage = 'https://cdn.foodrevolution.org/ff/og/ff-evergreen-opengraph-salespage-1200x1200-v202207019_v2.png',
  facebookDescription = 'The Fantastic Fungi Impact Kit takes you on an immersive journey into the magical earth beneath our feet — to an underground network that can heal and save our planet! Learn more here:',
  twitterDescription = 'The Fantastic Fungi Impact Kit takes you on an immersive journey to an underground network that can heal and save our planet. Learn more here:',
  twitterImg = 'https://cdn.foodrevolution.org/ff/og/ff-evergreen-opengraph-salespage-1200x675-v202207019.png',
  pinterestDescription = 'The Fantastic Fungi Impact Kit takes you on an immersive journey into the magical earth beneath our feet — to an underground network that can heal and save our planet. Learn more here:',
  pinterestImg = 'https://cdn.foodrevolution.org/ff/og/ff-evergreen-opengraph-salespage-1000x1500-v202207019.png',
  ...props
}) {
  const {bucket, email, registrationId} = useUserState();
  const {
    phases: {
      default: defaultSchedule = {
        phase: 'before',
        launchStart: '',
        launchEnd: '',
        launchTitle: ''
      },
      sales: salesSchedule = {
        phase: 'during',
        salesEnd: '',
        isSalesDay: false
      },
      webinar: webinarSchedule = {
        phase: 'before'
      }
    }
  } = usePhaseState();

  const qs = typeof window !== 'undefined' ? queryString.parse(window.location.search) : {};
  const {t} = qs.t ? qs : {};

  const isCartClose = new Date(webinarSchedule.endOfSalesDay * 1000 - 86400000).getTime() < new Date().getTime();
  const salesEnd = new Date(webinarSchedule.endOfSalesDay * 1000).getTime();
  const salesTimerDate = timerOverride ? timerOverride : salesEnd;

  const salesFullPrice =
    (!email && !registrationId && !ctaOverride) ||
    (!ctaOverride && webinarSchedule.phase === 'after') ||
    ctaOverride === 'sales197';

  const productID = 'HF-FF-IK';
  const productSku = 'HF-FF-IK';
  const productName = 'Fantastic Fungi Impact Kit';
  const productSlug =
    variant === 'ad-ik' ? 'ff-impactkit-us' : t || !salesFullPrice ? 'ff-impactkit-launch-st' : 'ff-impactkit-st';
  const productValue = t || !salesFullPrice ? 97 : 197;
  const bonusValue = '400';

  useEffect(() => {
    track('Product Viewed', {
      product_id: productID,
      sku: productSku,
      name: productName,
      variant: productSlug,
      value: productValue,
      currency: 'usd'
    });
  }, [salesFullPrice]);

  function ImpactCheckoutButton({className = null, ...props}) {
    return (
      <CheckoutButton color="cta-ff" sku={productSku} slug={productSlug} price={productValue} className={className}>
        Buy Now
      </CheckoutButton>
    );
  }

  function Timer({className = null, ...props}) {
    return (
      <>
        <div className={classNames('section-countdown text-center', className)}>
          <Row className="d-flex align-items-center justify-content-center m-0">
            <Col xs="12">
              <h5 className="section-heading text-ff-orange mb-3">Your Special Discount Ends In:</h5>
            </Col>
            <Col xs="12" className="timer countdown-dark">
              <CountdownTimer time={salesTimerDate} />
            </Col>
          </Row>
        </div>
      </>
    );
  }

  useBalanceText();
  return (
    <div className={classNames(`page d-flex flex-column`, noShareButtons ? `page-nosocial` : null)}>
      <PageMeta title={pageTitle} description={pageDescription} url={shareUrl} ogImage={ogImage} />

      <div className="page-content">
        <SectionTriangle
          id="ff-impact-header-1"
          color="black"
          className={variant === 'ad-ik' ? 'section-triangle-first promo-header-override' : 'section-triangle-first'}
          imgUrl={require('static/backgrounds/ff-saleslanding-image-header-backdrop-landon-parenteau-EEuDMqRYbx0-unsplash.jpg')}
          innerClass="p-0"
        />

        <SectionTriangle id="ff-impact-header-2" color="white" type="top">
          <Container>
            <div
              className={classNames(
                `offset-box background-white-80 border-ff-gray-10-solid text-center p-3 p-md-4 p-lg-5 ${
                  variant === 'ad-ik' ? 'offset-box-promo' : ''
                }`
              )}
            >
              <Row>
                <Col>
                  <h3 className="section-heading video-heading">
                    Dig Deep into the Medicinal, Therapeutic, Environmental, and Culinary Powers of the Mushroom Kingdom
                    with The <i>Fantastic Fungi</i> Impact Kit
                  </h3>
                  <HeaderVideo
                    url={
                      variant === 'ad-ik'
                        ? 'https://youtu.be/lr0NE6upRdQ'
                        : !salesFullPrice && isCartClose
                        ? 'https://www.youtube.com/watch?v=acj8Kq5tNms'
                        : !salesFullPrice && !isCartClose
                        ? 'https://www.youtube.com/watch?v=uj_LUZcJYeE'
                        : 'https://www.youtube.com/watch?v=ASk8aVfPN8A'
                    }
                    noFixed={noFixedVideo}
                    sales
                    sku={productSku}
                    slug={productSlug}
                    price={productValue}
                    label="FF - Sales"
                  />
                </Col>
              </Row>
              <Row className="offset-box-cta">
                <Col>
                  {!salesFullPrice ? (
                    <>
                      <h3 className="mt-0 mb-3">
                        Regular Price: <del>$197</del>
                      </h3>
                      <h2 className="mt-0 mb-3">Limited Time Discount Price: $97</h2>
                    </>
                  ) : (
                    <>
                      <h2 className="mt-0 mb-3">Your Price: $197</h2>
                    </>
                  )}
                  <ImpactCheckoutButton />
                  {variant !== 'ad-ik' && !salesFullPrice && !ctaNoTimer ? (
                    <>
                      <h5 className="mt-4">Your special discount ends in:</h5>
                      <CountdownTimer time={salesTimerDate} className="countdown-dark" />
                    </>
                  ) : null}
                  <SalesDisclaimer />
                </Col>
              </Row>
            </div>
          </Container>
        </SectionTriangle>

        <SalesCopy version="sales" variant={variant}>
          <div className="cta-box border-ff-orange-5-solid text-center">
            {!salesFullPrice ? (
              <SectionTriangle id="ff-impact-cta-a" color="ff-cream" type="bottom" innerClass="pt-4 pt-lg-5">
                <h5 className="section-only-heading">
                  Regular Price: <del>$197</del>
                </h5>
              </SectionTriangle>
            ) : null}
            <SectionTriangle
              id="ff-impact-cta-b"
              color="white"
              type="bottom"
              innerClass={!salesFullPrice ? 'text-ff-orange' : 'text-ff-orange pt-4 pt-lg-5'}
            >
              {!salesFullPrice ? (
                <>
                  <h3 className="section-heading mb-2">Limited Time Discount Price:</h3>
                  <h2 className="text-h1 my-0">$97</h2>
                </>
              ) : (
                <>
                  <h2 className="text-h1 my-0">Your Price: $197</h2>
                </>
              )}
            </SectionTriangle>
            <SectionTriangle id="ff-impact-cta-c" color="ff-cream" type="bottom">
              <p className="m-0">
                16 Masterclasses with experts from <br className="d-none d-sm-block" />
                the <i>Fantastic Fungi</i> community, including <br className="d-none d-sm-block" />
                written transcripts and downloadable MP3s
              </p>
              <hr className="border-color-black-50 w-25 my-4" />
              <p className="m-0">
                Extended interviews and deleted <br className="d-none d-sm-block" />
                scenes from <i>Fantastic Fungi</i>
              </p>
              <hr className="border-color-black-50 w-25 my-4" />
              {variant !== 'ad-ik' ? (
                <>
                  <p className="m-0">
                    A digital special event version <br className="d-none d-sm-block" />
                    of the <i>Fantastic Fungi</i> film
                  </p>
                  <hr className="border-color-black-50 w-25 my-4" />
                </>
              ) : null}
              <p className="m-0">Bonus gifts worth ${bonusValue}</p>
            </SectionTriangle>
            <SectionTriangle id="ff-impact-cta-d" color="white">
              {variant !== 'ad-ik' && !salesFullPrice && !ctaNoTimer ? <Timer /> : null}
              <ImpactCheckoutButton className={!salesFullPrice ? 'mt-4 mb-4' : 'mb-4'} />
              <SalesDisclaimer className="mt-0" />
            </SectionTriangle>
          </div>
        </SalesCopy>
      </div>

      {noExitIntent ? null : <>{!salesFullPrice ? <ModalExitSales /> : null}</>}

      <div className="footer-content mt-auto">
        <Footer partner />
        {noShareButtons ? null : (
          <ShareButtons
            shareUrl={shareUrl}
            shareImg={ogImage}
            facebookDescription={facebookDescription}
            twitterDescription={twitterDescription}
            pinterestDescription={pinterestDescription}
            pinterestImg={pinterestImg}
            floating
          />
        )}
      </div>
    </div>
  );
}
